import React from 'react';
import styled from 'styled-components';
import { blocks, reasons } from './form';

const reasonLabels = Object.fromEntries(reasons);
const blockLabels = Object.fromEntries(blocks);

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--tc-pill-color);
  background: var(--tc-pill-background);
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;
const tones = {
  success: ['#1d6b3a', '#e3f4e8'],
  warning: ['#8a4512', '#fdebd8'],
  danger: ['#a32222', '#fde4e4'],
  info: ['#1c4b75', '#e2eefa'],
};

/** Situação da placa neste instante, conforme a disponibilidade calculada pela API. */
export function availabilityStatus(data) {
  const state = data.availability?.state ?? (data.available ? 'AVAILABLE' : 'UNAVAILABLE');
  const reason = reasonLabels[data.unavailabilityReason];
  if (state === 'AVAILABLE') return ['success', 'Disponível'];
  if (state === 'EXPIRED') return ['success', 'Disponível · período encerrado'];
  if (state === 'SCHEDULED') return ['info', `Disponível · ${reason} agendada`];
  if (state === 'INVALID' || !reason) return ['danger', 'Revisar configuração'];
  return [
    data.unavailabilityReason === 'BLOCKED' ? 'danger' : 'warning',
    `Indisponível · ${reason}`,
  ];
}

export function StatusPill({ data }) {
  const [tone, label] = availabilityStatus(data);
  const [color, background] = tones[tone];
  return (
    <Pill
      role="status"
      data-tone={tone}
      style={{ '--tc-pill-color': color, '--tc-pill-background': background }}
    >
      {label}
    </Pill>
  );
}

const Container = styled.div`
  font-size: 14px;
  .tc-summary-reason {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    padding: 12px 24px;
    background: #fdf3e6;
    border-bottom: 1px solid #f3e1c9;
    color: #6b5a45;
    strong {
      color: #8a4512;
    }
    .tc-summary-period {
      color: #1f2933;
    }
  }
  dl {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px 24px;
    margin: 0;
    padding: 18px 24px;
  }
  dt {
    font-size: 13px;
    font-weight: 400;
    color: #606775;
    margin-bottom: 4px;
  }
  dd {
    margin: 0;
    font-weight: 600;
    color: #1f2933;
    overflow-wrap: anywhere;
  }
  dd a {
    color: #2f5f9e;
  }
  .tc-summary-empty {
    font-weight: 400;
    color: #8a929c;
  }
  @media (max-width: 900px) {
    dl {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 520px) {
    .tc-summary-reason {
      padding: 12px 16px;
    }
    dl {
      grid-template-columns: 1fr;
      padding: 16px;
    }
  }
`;
/** "2026-10-08T14:00:00.000" (São Paulo) → "08/10/2026 14:00". */
const localLabel = (value) =>
  value
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)} ${value.slice(11, 16)}`
    : '';
const empty = <span className="tc-summary-empty">Não informado</span>;

/** Visualização da configuração salva: motivo da indisponibilidade e dados do responsável. */
export default function Summary({ form }) {
  const phone = form.contactPhone.trim();
  return (
    <Container>
      {!form.available && (
        <p className="tc-summary-reason">
          <span>
            Motivo: <strong>{reasonLabels[form.unavailabilityReason] || 'Não informado'}</strong>
          </span>
          {['MAINTENANCE', 'TIME_OFF'].includes(form.unavailabilityReason) && (
            <span>
              Período:{' '}
              <strong className="tc-summary-period">
                {localLabel(form.unavailableFrom)} → {localLabel(form.unavailableUntil)}
              </strong>
            </span>
          )}
          {form.unavailabilityReason === 'DEDICATED' && (
            <span>
              Cliente:{' '}
              <strong className="tc-summary-period">
                {form.dedicatedClient
                  ? `${form.dedicatedClient.name}${form.dedicatedClient.cnpj ? ` — ${form.dedicatedClient.cnpj}` : ''}`
                  : 'Não informado'}
              </strong>
            </span>
          )}
          {form.unavailabilityReason === 'BLOCKED' && (
            <span>
              Bloqueio:{' '}
              <strong className="tc-summary-period">
                {blockLabels[form.blockReason] || 'Não informado'}
              </strong>
            </span>
          )}
        </p>
      )}
      <dl>
        <div>
          <dt>Veículo de corredor</dt>
          <dd>{form.corridor ? 'Sim' : 'Não'}</dd>
        </div>
        <div>
          <dt>Responsável (filial)</dt>
          <dd>{form.responsible?.name || empty}</dd>
        </div>
        <div>
          <dt>Contato</dt>
          <dd>{phone ? <a href={`tel:${phone.replace(/[^+\d]/g, '')}`}>{phone}</a> : empty}</dd>
        </div>
        <div>
          <dt>Centro de custo</dt>
          <dd>{form.costCenter?.name || empty}</dd>
        </div>
      </dl>
    </Container>
  );
}
